import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { History } from "lucide-react";
import ClubLeaderboards from "../components/ClubLeaderboards";
import { api, apiError, cn, fmtDate, publicAppUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import WhatsAppShareButton, { SIGN_OFF } from "../components/WhatsAppShareButton";
import InstagramShareSheet, { InstagramButton } from "../components/InstagramShareSheet";
import { drawMyGamesCard, myGamesCaption } from "../lib/reportImage";
import TopBar from "../components/TopBar";
import { Card, Chip, Empty, Skeleton } from "../components/ui";

const OUTCOME = {
  won: { label: "Won", tone: "success" },
  lost: { label: "Lost", tone: "danger" },
  tied: { label: "Tied", tone: "warning" },
  common: { label: "Common player", tone: "default" },
};

const TABS = [
  { key: "me", label: "My games" },
  { key: "club", label: "Club" },
];

/** Stats tab: the player's own match history, and the club's leaderboards. */
export default function MyMatchesPage() {
  const loc = useLocation();
  // A shared link (/stats?tab=club) opens straight on that tab
  const [tab, setTab] = useState(() => {
    const fromLink = new URLSearchParams(loc.search).get("tab");
    if (TABS.some((x) => x.key === fromLink)) return fromLink;
    try { return sessionStorage.getItem("cricso-stats-tab") || "me"; } catch { return "me"; }
  });
  const pick = (key) => {
    setTab(key);
    try { sessionStorage.setItem("cricso-stats-tab", key); } catch { /* private mode */ }
  };

  return (
    <div className="pb-24">
      <TopBar subtitle="Cricket stats" title={tab === "me" ? "My games" : "Club performance"} />
      <div className="px-5 mb-4">
        <div className="grid grid-cols-2 gap-1 p-1 bg-black/[0.04] rounded-2xl" role="tablist">
          {TABS.map((x) => (
            <button
              key={x.key}
              role="tab"
              aria-selected={tab === x.key}
              onClick={() => pick(x.key)}
              className={cn("h-10 rounded-xl text-sm font-semibold", tab === x.key ? "bg-white shadow-sm text-ink" : "text-ink-muted")}
              data-testid={`stats-tab-${x.key}`}
            >
              {x.label}
            </button>
          ))}
        </div>
      </div>
      {tab === "me" ? <MyGames /> : <ClubLeaderboards />}
    </div>
  );
}

/** The logged-in player's own match history: every scored match they played, with their figures. */
function MyGames() {
  const nav = useNavigate();
  const { user, club } = useAuth();
  const [data, setData] = useState(null);
  const [igOpen, setIgOpen] = useState(false);

  useEffect(() => {
    api.get("/stats/me/matches")
      .then(({ data }) => setData(data))
      .catch((err) => { toast.error(apiError(err)); setData({ linked: true, matches: [] }); });
  }, []);

  const matches = data?.matches || [];
  const totals = matches.reduce((t, m) => ({
    won: t.won + (m.outcome === "won" ? 1 : 0),
    runs: t.runs + m.batting.reduce((a, b) => a + b.runs, 0),
    wickets: t.wickets + m.bowling.reduce((a, b) => a + b.wickets, 0),
  }), { won: 0, runs: 0, wickets: 0 });

  const shareText = () => {
    const bats = matches.flatMap((m) => m.batting);
    const bowls = matches.flatMap((m) => m.bowling);
    const top = bats.reduce((b, x) => (!b || x.runs > b.runs || (x.runs === b.runs && !x.out) ? x : b), null);
    const best = bowls.reduce((b, x) => (!b || x.wickets > b.wickets || (x.wickets === b.wickets && x.runs < b.runs) ? x : b), null);
    const catches = matches.reduce((a, m) => a + m.fielding.catches + m.fielding.run_outs + m.fielding.stumpings, 0);
    const lines = [
      `🏏 *${user?.name || "My"} — My games*${club?.name ? ` (${club.name})` : ""}`,
      "",
      `Matches ${matches.length} • Won ${totals.won} • Runs ${totals.runs} • Wickets ${totals.wickets}`,
      [
        top && `Best ${top.runs}${top.out ? "" : "*"} (${top.balls})`,
        best && best.wickets > 0 && `Best bowling ${best.wickets}/${best.runs}`,
        catches > 0 && `${catches} dismissal${catches === 1 ? "" : "s"} in the field`,
      ].filter(Boolean).join(" • "),
      "",
      "*Recent matches*",
    ];
    matches.slice(0, 5).forEach((m, i) => {
      const result = m.outcome === "common" ? "Common player" : OUTCOME[m.outcome]?.label || "Live";
      lines.push(`${i + 1}. ${fmtDate(m.date)} • ${m.teams[0]} vs ${m.teams[1]} — ${result}`);
      const bat = m.batting.map((b) => `${b.runs}${b.out ? "" : "*"} (${b.balls})`).join(", ");
      const bowl = m.bowling.map((b) => `${b.wickets}/${b.runs} (${b.overs})`).join(", ");
      const figs = [bat && `Bat ${bat}`, bowl && `Bowl ${bowl}`].filter(Boolean).join(" • ");
      if (figs) lines.push(`   ${figs}`);
    });
    lines.push("", `📊 Club stats: ${publicAppUrl()}/stats?tab=club`, ...SIGN_OFF);
    return lines.filter((l, i, arr) => !(l === "" && arr[i - 1] === "")).join("\n");
  };

  return (
    <div>
      {!data ? (
        <div className="px-5 space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      ) : !data.linked ? (
        <div className="px-5">
          <Card className="p-0">
            <Empty icon={History} title="You're not a player in this club" subtitle="Match history shows up once you're added as a member." />
          </Card>
        </div>
      ) : matches.length === 0 ? (
        <div className="px-5">
          <Card className="p-0">
            <Empty icon={History} title="No matches yet" subtitle="Matches you play in will show up here once they're scored." />
          </Card>
        </div>
      ) : (
        <>
          <div className="px-5">
            <div className="rounded-3xl bg-ink text-white p-5 shadow-card grid grid-cols-4 gap-2" data-testid="my-matches-totals">
              <Total value={matches.length} label="Matches" />
              <Total value={totals.won} label="Won" />
              <Total value={totals.runs} label="Runs" />
              <Total value={totals.wickets} label="Wickets" />
            </div>
          </div>
          <div className="px-5 mt-3 grid grid-cols-2 gap-2">
            <WhatsAppShareButton getText={shareText} label="WhatsApp" data-testid="share-my-games" />
            <InstagramButton onClick={() => setIgOpen(true)} size="lg" data-testid="share-my-games-ig" />
          </div>
          <InstagramShareSheet
            open={igOpen}
            onClose={() => setIgOpen(false)}
            title="Share my games"
            makeImage={() => drawMyGamesCard({ name: user?.name, clubName: club?.name, matches })}
            caption={myGamesCaption({ name: user?.name, clubName: club?.name, matches })}
            fileName={`cricso-${user?.name || "my"}-games`}
          />
          <div className="px-5 mt-4 space-y-3">
            {matches.map((m) => (
              <MatchRow key={m.id} m={m} onClick={() => nav(`/scores/${m.id}`)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Total({ value, label }) {
  return (
    <div>
      <div className="text-2xl font-extrabold tabular-nums leading-tight">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">{label}</div>
    </div>
  );
}

function MatchRow({ m, onClick }) {
  const line = (team) => {
    const sc = m.scores.find((x) => x.team === team);
    return sc ? `${sc.runs}/${sc.wickets} (${sc.overs})` : "Yet to bat";
  };
  const outcome = OUTCOME[m.outcome];
  const { fielding: f } = m;
  const forTeam = (x) => (m.common ? <span className="text-ink-muted"> for {x.for}</span> : null);
  const fieldingBits = [
    f.catches && `${f.catches} catch${f.catches === 1 ? "" : "es"}`,
    f.run_outs && `${f.run_outs} run out${f.run_outs === 1 ? "" : "s"}`,
    f.stumpings && `${f.stumpings} stumping${f.stumpings === 1 ? "" : "s"}`,
  ].filter(Boolean);

  return (
    <Card className="py-4 px-5 cursor-pointer" onClick={onClick} data-testid={`my-match-${m.id}`}>
      <div className="flex items-center gap-2 mb-2">
        {m.status !== "completed"
          ? <Chip tone="danger">● Live</Chip>
          : outcome && <Chip tone={outcome.tone}>{outcome.label}</Chip>}
        <span className="text-xs text-ink-muted truncate">{m.title} • {fmtDate(m.date)}</span>
      </div>
      {m.teams.map((t, i) => (
        <div key={i} className="flex items-center justify-between py-0.5">
          <span className={cn("truncate", i === m.my_team ? "font-bold" : "font-semibold text-ink-muted")}>
            {t}{(m.common || i === m.my_team) && <span className="text-xs text-brand font-semibold"> • {m.common ? "Common" : "My team"}</span>}
          </span>
          <span className="font-bold tabular-nums text-sm">{line(i)}</span>
        </div>
      ))}
      {m.result && <div className="text-sm text-brand font-semibold mt-1">{m.result}</div>}

      <div className="mt-3 pt-3 border-t border-black/[0.06] space-y-1.5 text-sm">
        <Figure label="Bat">
          {m.batting.length === 0 ? <span className="text-ink-muted">Did not bat</span> : m.batting.map((bat, i) => (
            <div key={i}>
              <span className="font-bold tabular-nums">{bat.runs}{bat.out ? "" : "*"} ({bat.balls})</span>
              {forTeam(bat)}
              <span className="text-ink-muted">
                {` • ${bat.fours}×4 ${bat.sixes}×6 • SR ${bat.balls ? bat.strike_rate.toFixed(0) : "–"}`}
                {bat.out ? ` • ${bat.out}` : " • not out"}
              </span>
            </div>
          ))}
        </Figure>
        <Figure label="Bowl">
          {m.bowling.length === 0 ? <span className="text-ink-muted">Did not bowl</span> : m.bowling.map((bowl, i) => (
            <div key={i}>
              <span className="font-bold tabular-nums">{bowl.wickets}/{bowl.runs} ({bowl.overs})</span>
              {forTeam(bowl)}
              <span className="text-ink-muted">
                {` • Econ ${bowl.economy.toFixed(1)}`}
                {bowl.maidens ? ` • ${bowl.maidens} maiden${bowl.maidens === 1 ? "" : "s"}` : ""}
                {bowl.wides + bowl.no_balls ? ` • ${bowl.wides}wd ${bowl.no_balls}nb` : ""}
              </span>
            </div>
          ))}
        </Figure>
        {fieldingBits.length > 0 && (
          <Figure label="Field"><span className="font-semibold">{fieldingBits.join(" • ")}</span></Figure>
        )}
      </div>
    </Card>
  );
}

function Figure({ label, children }) {
  return (
    <div className="flex gap-3">
      <span className="w-10 shrink-0 text-[11px] uppercase tracking-wider text-ink-muted font-semibold pt-0.5">{label}</span>
      <span className="min-w-0">{children}</span>
    </div>
  );
}
